import React, { useState, createContext, useContext, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ProductView from "./components/ProductView/index";
import HomePage from "./pages/HomePage";
import LandingPage from "./pages/LandingPage";
import AboutPage from "./pages/AboutPage";
import SearchResultsPage from "./pages/SearchResultsPage";
import { useToken } from "./utils";
import ListingView from "./components/MyListingView";
import CreateListingWizard from "./pages/CreateListingWizard";
import EditListing from "./pages/EditListing";
import ProfilePage from "./pages/ProfilePage";
import VerifyEmailLandingPage from "./pages/VerifyEmailLanding";
import SsoCallbackPage from "./pages/SsoCallbackPage";
import ForgotPasswordPage from "./pages/ForgotPassword";
import ResetPasswordPage from "./pages/ResetPassword";
import FloatingMessagesButton from "./components/FloatingMessagesButton";
import UnverifiedEmailBanner from "./components/UnverifiedEmailBanner";
import MessagesPage from "./pages/MessagesPage";
import { GuestAuthProvider } from "./context/GuestAuthContext";
import ClerkAppBridge from "./components/ClerkAppBridge";
import { isClerkEnabled } from "./lib/clerk";
import DevVinColorSmoke from "./pages/DevVinColorSmoke";
import { isDevWizardHelpersEnabled } from "./pages/CreateListingWizard/listingWizardDevFill";

/**
 * Guests may browse listings (home, search, product detail, about) but cannot
 * message sellers or reach account-only tools without signing in. Messaging
 * mutations remain auth-gated on the API as well.
 */

// Dark mode context
type DarkModeContextType = {
  darkMode: boolean;
  setDarkMode: React.Dispatch<React.SetStateAction<boolean>>;
};
export const DarkModeContext = createContext<DarkModeContextType | undefined>(undefined);
export const useDarkMode = (): DarkModeContextType => {
  const ctx = useContext(DarkModeContext);
  // Fall back to a no-op default when the provider is absent (e.g. isolated tests)
  // so consumers don't need to wrap every render.
  return ctx ?? { darkMode: false, setDarkMode: () => {} };
};

const DARK_MODE_STORAGE_KEY = "p2pcarsales:darkMode";

function getInitialDarkMode(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const stored = window.localStorage.getItem(DARK_MODE_STORAGE_KEY);
    if (stored !== null) return stored === "true";
  } catch {
    // localStorage may be unavailable (SSR, privacy mode); fall through.
  }
  return window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ?? false;
}

/** Redirect unauthenticated visitors to sign-in for account-only surfaces. */
function RequireAuth({
  children,
  token,
}: {
  children: React.ReactElement;
  token: string | null;
}) {
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function App() {
  const { token, setToken } = useToken();
  const [darkMode, setDarkMode] = useState<boolean>(getInitialDarkMode);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    try {
      window.localStorage.setItem(DARK_MODE_STORAGE_KEY, String(darkMode));
    } catch {
      // Ignore storage errors (e.g. quota, private browsing).
    }
  }, [darkMode]);

  return (
    <DarkModeContext.Provider value={{ darkMode, setDarkMode }}>
      <BrowserRouter>
        {isClerkEnabled() ? (
          <ClerkAppBridge setToken={setToken} hasAppToken={Boolean(token)} />
        ) : null}
        <GuestAuthProvider token={token} setToken={setToken}>
          {token && <UnverifiedEmailBanner />}
          <Routes>
            {/* Listing-first marketplace home for guests and members. */}
            <Route
              path="/"
              element={<HomePage darkMode={darkMode} />}
            />

            {/* Marketing / selling pitch moved off the home page. */}
            <Route path="/about" element={<AboutPage />} />

            {/* Public listing browse — available without a session. */}
            <Route path="/search" element={<SearchResultsPage darkMode={darkMode} />} />
            <Route path="/product/:id" element={<ProductView />} />

            {/* Auth entry points (modal on listing home). Authed users
                already have a session — send them home instead of the guest
                Sign In / Sign Up chrome. */}
            <Route
              path="/login"
              element={
                token ? <Navigate to="/" replace /> : <LandingPage initialAuth="signin" />
              }
            />
            <Route
              path="/signup"
              element={
                token ? <Navigate to="/" replace /> : <LandingPage initialAuth="signup" />
              }
            />

            {/* Magic-link landing — recipients may not be authenticated yet. */}
            <Route
              path="/verify-email"
              element={<VerifyEmailLandingPage hasToken={Boolean(token)} />}
            />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Clerk OAuth redirect completion (public; session exchange happens in ClerkAppBridge). */}
            {isClerkEnabled() ? (
              <Route path="/sso-callback" element={<SsoCallbackPage />} />
            ) : null}

            {/* Account-only surfaces. */}
            <Route
              path="/mylistings"
              element={
                <RequireAuth token={token}>
                  <ListingView />
                </RequireAuth>
              }
            />
            <Route
              path="/newlisting"
              element={
                <RequireAuth token={token}>
                  <CreateListingWizard />
                </RequireAuth>
              }
            />
            <Route
              path="/edit-listing/:id"
              element={
                <RequireAuth token={token}>
                  <EditListing />
                </RequireAuth>
              }
            />
            <Route
              path="/profile"
              element={
                <RequireAuth token={token}>
                  <ProfilePage />
                </RequireAuth>
              }
            />
            <Route
              path="/messages"
              element={
                <RequireAuth token={token}>
                  <MessagesPage />
                </RequireAuth>
              }
            />

            {/* Local-only UX smoke pages (NODE_ENV=development). */}
            {isDevWizardHelpersEnabled() ? (
              <Route path="/dev/vin-color-smoke" element={<DevVinColorSmoke />} />
            ) : null}

            {/* Unknown paths: send everyone to the listing home. */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          {token && <FloatingMessagesButton />}
        </GuestAuthProvider>
      </BrowserRouter>
    </DarkModeContext.Provider>
  );
}

export default App;
